import { useEffect, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { FileTypeEnum } from '@ecdlink/graphql';
import { DocumentDto, DocumentTypeDto } from '@ecdlink/core';
import {
  Button,
  classNames,
  Dialog,
  DialogPosition,
  Divider,
  Dropdown,
  DropDownOption,
  IMAGE_WIDTH,
  ImageInput,
  renderIcon,
  Typography,
} from '@ecdlink/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useStaticData } from '@/hooks/useStaticData';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import {
  ChildRegistrationInformationModel,
  childRegistrationInformationSchema,
  initialChildRegistrationInformation,
} from '@/schemas/child/child-registration/child-registration-information';
import { imageExtensions } from '@/constants/AcceptableImageExtensions';
import { PhotoPrompt } from '@/components/photo-prompt/photo-prompt';
import { useAppDispatch } from '@/store';
import { staticDataThunkActions } from '@/store/static-data';
import { documentActions, documentThunkActions } from '@/store/document';
import { ChildRegistrationInformationProps } from './child-registration-information.types';

export const ChildRegistrationInformation: React.FC<
  ChildRegistrationInformationProps
> = ({
  child,
  childName = 'Child',
  onSubmit,
  submitButtonText = 'Next',
  submitButtonIcon = 'ArrowCircleRightIcon',
  canEdit = false,
}) => {
  const { isOnline } = useOnlineStatus();
  const appDispatch = useAppDispatch();
  const registrationDocumentTypes: DropDownOption<string>[] = [
    {
      label: 'Child Birth Certificate',
      value: 'birthCertificate',
    },
    {
      label: 'Clinic card',
      value: 'clinicCard',
    },
  ];
  const { getDocumentTypeIdByEnum } = useStaticData();
  const registrationDocumentTypeEnums: string[] = [
    'CHILD_CLINIC_CARD',
    'CHILD_BIRTH_CERTIFICATE',
  ];
  const [
    selectedRegistrationDocumentType,
    setSelectedRegistrationDocumentType,
  ] = useState<string>();
  const [
    registrationDocumentBase64String,
    setRegistrationDocumentBase64String,
  ] = useState<string>('');
  const [registrationFormBase64String, setRegistrationFormBase64String] =
    useState<string>('');
  const [photoActionBarVisible, setPhotoActionBarVisible] =
    useState<boolean>(false);
  const [
    registrationFormPhotoActionBarVisible,
    setRegistrationFormPhotoActionBarVisible,
  ] = useState<boolean>(false);
  const [currentRegistrationDocument, setCurrentRegistrationDocument] =
    useState<DocumentDto>();
  const [currentRegistrationForm, setCurrentRegistrationForm] =
    useState<DocumentDto>();
  const [
    registrationInformationDocuments,
    setRegistrationInformationDocuments,
  ] = useState<ChildRegistrationInformationModel[]>([]);

  const {
    setValue: setChildRegistrationInformationFormValue,
    getValues: getChildRegistrationInformationFormValues,
    register: childRegistrationInformationFormRegister,
    control: childRegistrationInformationFormControl,
    trigger,
  } = useForm({
    resolver: yupResolver(childRegistrationInformationSchema),
    mode: 'onBlur',
    defaultValues: initialChildRegistrationInformation,
  });
  const convertLabelToValue = (sentence: string): string => {
    return sentence
      .toLowerCase()
      .replace(/[^a-zA-Z0-9 ]+/g, '')
      .split(' ')
      .slice(1)
      .filter((word) => word)
      .map((word, index) =>
        index === 0 ? word : word[0].toUpperCase() + word.slice(1)
      )
      .join('');
  };

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!isOnline) return;
      const documentTypes = await appDispatch(
        staticDataThunkActions.getDocumentTypes({})
      ).unwrap();
      const localRequiredDocumentTypes = documentTypes.filter((documentType) =>
        registrationDocumentTypeEnums.includes(documentType.enumId)
      ) as DocumentTypeDto[];

      const localStateDocuments = await appDispatch(
        documentThunkActions.getDocuments({})
      ).unwrap();

      const selectedRegistrationDocumentForCurrentChild =
        localStateDocuments.find(
          (document) =>
            document.userId === child?.id &&
            document.isActive !== false &&
            localRequiredDocumentTypes!.some(
              (documentType) => documentType.id === document.documentTypeId
            )
        );
      const selectedRegistrationFormForCurrentChild = localStateDocuments.find(
        (document) =>
          document.userId === child?.id &&
          document.isActive !== false &&
          document.documentTypeId ===
            getDocumentTypeIdByEnum(FileTypeEnum.ChildRegistrationForm)
      );
      setCurrentRegistrationForm(selectedRegistrationFormForCurrentChild);
      setCurrentRegistrationDocument(
        selectedRegistrationDocumentForCurrentChild
      );

      const selectedDocumentType = selectedRegistrationDocumentForCurrentChild
        ? convertLabelToValue(
            localRequiredDocumentTypes!.find(
              (documentType) =>
                documentType.id ===
                selectedRegistrationDocumentForCurrentChild?.documentTypeId
            )?.description!
          )
        : '';

      setChildRegistrationInformationFormValue(
        'registrationDocumentBase64String',
        selectedRegistrationDocumentForCurrentChild?.reference ?? ''
      );
      setChildRegistrationInformationFormValue(
        'registrationFormBase64String',
        selectedRegistrationFormForCurrentChild?.reference ?? ''
      );
      setChildRegistrationInformationFormValue(
        'registrationDocumentType',
        selectedDocumentType
      );

      setSelectedRegistrationDocumentType(selectedDocumentType);

      setRegistrationFormBase64String(
        selectedRegistrationFormForCurrentChild?.reference
          ? selectedRegistrationFormForCurrentChild.reference
          : selectedRegistrationFormForCurrentChild?.file!
      );
      setRegistrationDocumentBase64String(
        selectedRegistrationDocumentForCurrentChild?.reference
          ? selectedRegistrationDocumentForCurrentChild?.reference
          : selectedRegistrationDocumentForCurrentChild?.file!
      );
    };
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentRegistrationDocument =
      getChildRegistrationInformationFormValues();
    if (
      currentRegistrationDocument.registrationDocumentTypeEnumId ===
        FileTypeEnum.ChildClinicCard ||
      currentRegistrationDocument.registrationDocumentTypeEnumId ===
        FileTypeEnum.ChildBirthCertificate
    ) {
      setRegistrationDocument(
        currentRegistrationDocument.registrationDocumentBase64String,
        false
      );
    }
    if (
      currentRegistrationDocument.registrationDocumentTypeEnumId ===
      FileTypeEnum.ChildRegistrationForm
    ) {
      setRegistrationDocument(
        currentRegistrationDocument.registrationDocumentBase64String,
        true
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getChildRegistrationInformationFormValues().registrationDocumentType]);

  const { isValid } = useFormState({
    control: childRegistrationInformationFormControl,
  });

  const filterDocuments = (
    registrationDocumentForms: ChildRegistrationInformationModel[]
  ): ChildRegistrationInformationModel[] => {
    const results = registrationDocumentForms.reduce<{
      RegistrationForm: ChildRegistrationInformationModel | null;
      alternate: ChildRegistrationInformationModel | null;
    }>(
      (previousDocument, currentDocument) => {
        if (
          currentDocument.registrationDocumentTypeEnumId ===
          FileTypeEnum.ChildRegistrationForm
        )
          previousDocument.RegistrationForm = currentDocument;
        else if (
          currentDocument.registrationDocumentTypeEnumId ===
            FileTypeEnum.ChildClinicCard ||
          currentDocument.registrationDocumentTypeEnumId ===
            FileTypeEnum.ChildBirthCertificate
        )
          previousDocument.alternate = currentDocument;
        return previousDocument;
      },
      {
        RegistrationForm: null,
        alternate: null,
      }
    );
    return Object.values(results).filter(
      (document): document is ChildRegistrationInformationModel =>
        Boolean(document)
    );
  };

  const setRegistrationDocument = async (
    imageUrl: string,
    isRegistrationForm: boolean
  ) => {
    if (isRegistrationForm) {
      setChildRegistrationInformationFormValue(
        'registrationFormBase64String',
        imageUrl
      );
      setRegistrationFormBase64String(imageUrl);
    } else {
      setChildRegistrationInformationFormValue(
        'registrationDocumentBase64String',
        imageUrl
      );
      setRegistrationDocumentBase64String(imageUrl);
    }
    const selectedFileName = isRegistrationForm
      ? 'F4-registrationForm.png'
      : `${
          getChildRegistrationInformationFormValues().registrationDocumentType
        }.png`;
    const selectedFileType = isRegistrationForm
      ? FileTypeEnum.ChildRegistrationForm
      : getChildRegistrationInformationFormValues().registrationDocumentType ===
        'birthCertificate'
      ? FileTypeEnum.ChildBirthCertificate
      : FileTypeEnum.ChildClinicCard;
    const typeId = await getDocumentTypeIdByEnum(selectedFileType);
    const registrationDocumentForms = {
      registrationDocumentType: selectedFileType,
      registrationDocumentTypeId: typeId!,
      registrationDocumentTypeEnumId: selectedFileType,
      registrationDocumentBase64String:
        getChildRegistrationInformationFormValues()
          .registrationDocumentBase64String,
      registrationFormBase64String:
        getChildRegistrationInformationFormValues()
          .registrationFormBase64String,
      registrationDocumentFileName: selectedFileName,
    } as ChildRegistrationInformationModel;
    setRegistrationInformationDocuments((currentList) => [
      ...currentList,
      registrationDocumentForms,
    ]);
    isRegistrationForm
      ? setRegistrationFormPhotoActionBarVisible(false)
      : setPhotoActionBarVisible(false);
  };

  const registrationDocumentOverride = async (
    registrationFile: DocumentDto
  ) => {
    await appDispatch(
      documentActions.updateDocument({
        id: registrationFile?.id,
        userId: registrationFile?.userId,
        createdUserId: registrationFile?.createdUserId,
        documentTypeId: registrationFile?.documentTypeId!,
        name: registrationFile?.name,
        reference: registrationFile?.reference,
        workflowStatusId: registrationFile?.workflowStatusId,
        isActive: false,
      })
    );
    if (registrationFile?.reference) {
      const registrationFileLink = registrationFile?.reference!.split('/')!;
      const registrationFileName =
        registrationFileLink[registrationFileLink.length - 1];

      const recordDeleted = true;
      await appDispatch(
        documentThunkActions.deleteDocument({
          fileId: registrationFile?.id!,
          fileName: registrationFileName,
        })
      ).unwrap();
      if (recordDeleted) return true;
      else return false;
    }
  };

  const handleFormSubmit = async () => {
    if (isValid) {
      const registrationDocumentUpdated = registrationInformationDocuments.find(
        (document) =>
          document.registrationDocumentType ===
            FileTypeEnum.ChildBirthCertificate ||
          document.registrationDocumentType === FileTypeEnum.ChildClinicCard
      );
      const registrationFileUpdated = registrationInformationDocuments.find(
        (document) =>
          document.registrationDocumentType ===
          FileTypeEnum.ChildRegistrationForm
      );
      if (
        registrationDocumentUpdated !== undefined &&
        currentRegistrationDocument !== undefined
      )
        await registrationDocumentOverride(currentRegistrationDocument);
      if (
        registrationFileUpdated !== undefined &&
        currentRegistrationForm !== undefined
      )
        await registrationDocumentOverride(currentRegistrationForm);

      const cleanedRegistrationDocumentsList = filterDocuments(
        registrationInformationDocuments
      );
      setRegistrationInformationDocuments(cleanedRegistrationDocumentsList);
      return onSubmit(cleanedRegistrationDocumentsList);
    }
  };

  return (
    <div className="h-full bg-white px-4 pt-2 pb-4">
      <Typography type={'h1'} text={childName} color={'primary'} />
      <Typography
        type={'h2'}
        text={'Registration Information'}
        color={'textMid'}
      />
      <div>
        <Dropdown
          placeholder="Select Registration Document Type"
          list={registrationDocumentTypes}
          fillType="clear"
          label={'Registration Document Type'}
          disabled={!canEdit || !isOnline}
          fullWidth
          className="mt-3"
          selectedValue={selectedRegistrationDocumentType}
          onChange={(item: any) => {
            setChildRegistrationInformationFormValue(
              'registrationDocumentType',
              item
            );
            setSelectedRegistrationDocumentType(item);
            trigger();
          }}
        />
        {canEdit && (
          <ImageInput<ChildRegistrationInformationModel>
            acceptedFormats={imageExtensions}
            resolutionLimit={IMAGE_WIDTH}
            label="Registration document"
            nameProp="registrationDocumentBase64String"
            icon="CameraIcon"
            iconContainerColor={'tertiary'}
            className="py-4"
            currentImageString={registrationDocumentBase64String}
            register={childRegistrationInformationFormRegister}
            overrideOnClick={() => setPhotoActionBarVisible(true)}
            onValueChange={(imageString: string) => {
              setRegistrationDocumentBase64String(() => imageString);
              trigger();
            }}
          />
        )}
        {canEdit && (
          <ImageInput<ChildRegistrationInformationModel>
            acceptedFormats={imageExtensions}
            resolutionLimit={IMAGE_WIDTH}
            label="Signed child registration form (F4)"
            nameProp="registrationFormBase64String"
            icon="CameraIcon"
            iconContainerColor={'tertiary'}
            className="py-4"
            currentImageString={registrationFormBase64String}
            register={childRegistrationInformationFormRegister}
            overrideOnClick={() =>
              setRegistrationFormPhotoActionBarVisible(true)
            }
            onValueChange={(imageString: string) => {
              setRegistrationFormBase64String(() => imageString);
              trigger();
            }}
          />
        )}
        <div className={'py-4'}>
          <Divider></Divider>
        </div>
        {canEdit && (
          <Button
            onClick={handleFormSubmit}
            className="w-full"
            size="small"
            color="primary"
            type="filled"
            disabled={!isOnline}
          >
            {renderIcon(submitButtonIcon, classNames('h-5 w-5 text-white'))}
            <Typography
              type="h6"
              className="ml-2"
              text={submitButtonText}
              color="white"
            />
          </Button>
        )}
        <Dialog
          visible={
            photoActionBarVisible || registrationFormPhotoActionBarVisible
          }
          position={DialogPosition.Middle}
          stretch
        >
          <PhotoPrompt
            title="Child Registration Information form"
            resolutionLimit={IMAGE_WIDTH}
            onClose={() => setPhotoActionBarVisible(false)}
            onAction={(imageUrl: string) =>
              setRegistrationDocument(
                imageUrl,
                registrationFormPhotoActionBarVisible
              )
            }
            onDelete={
              !photoActionBarVisible && registrationFormPhotoActionBarVisible
                ? registrationFormBase64String !== ''
                  ? () => {
                      setChildRegistrationInformationFormValue(
                        'registrationFormBase64String',
                        ''
                      );
                      setRegistrationFormBase64String('');
                      setRegistrationFormPhotoActionBarVisible(false);
                    }
                  : undefined
                : registrationDocumentBase64String !== ''
                ? () => {
                    setChildRegistrationInformationFormValue(
                      'registrationDocumentBase64String',
                      ''
                    );
                    setRegistrationDocumentBase64String('');
                    setPhotoActionBarVisible(false);
                  }
                : undefined
            }
          ></PhotoPrompt>
        </Dialog>
      </div>
    </div>
  );
};
